import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AxiosError } from 'axios';
import { Loader2 } from 'lucide-react';
import type { ApiError } from '@pms/shared';
import { requirementsService, type RequirementCategory } from '@/services/requirements.service';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

function errMsg(e: unknown): string {
  return (e as AxiosError<ApiError>).response?.data?.error?.message ?? 'Something went wrong';
}

interface FormValues {
  title: string;
  description: string;
  category_id: string;
  priority: 'low' | 'medium' | 'high' | 'critical';
  quantity: number;
  unit: string;
  estimated_cost: string;
  due_date: string;
  notes: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
}

export function CreateRequirementModal({ open, onClose }: Props) {
  const queryClient = useQueryClient();

  const { data: categories = [] } = useQuery<RequirementCategory[]>({
    queryKey: ['requirements', 'categories'],
    queryFn: () => requirementsService.listCategories(),
  });

  const { register, handleSubmit, reset, setValue, watch, formState: { errors } } = useForm<FormValues>({
    defaultValues: {
      title: '',
      description: '',
      category_id: '',
      priority: 'medium',
      quantity: 1,
      unit: 'pieces',
      estimated_cost: '',
      due_date: '',
      notes: '',
    },
  });

  const priorityValue = watch('priority');
  const categoryValue = watch('category_id');

  useEffect(() => {
    if (open) {
      reset({
        title: '',
        description: '',
        category_id: '',
        priority: 'medium',
        quantity: 1,
        unit: 'pieces',
        estimated_cost: '',
        due_date: '',
        notes: '',
      });
    }
  }, [open, reset]);

  const mutation = useMutation({
    mutationFn: async (values: FormValues) => {
      await requirementsService.createRequirement({
        title: values.title.trim(),
        description: values.description.trim() || undefined,
        category_id: values.category_id,
        priority: values.priority,
        quantity: Number(values.quantity),
        unit: values.unit.trim(),
        estimated_cost: values.estimated_cost ? Number(values.estimated_cost) : undefined,
        due_date: values.due_date || undefined,
        notes: values.notes.trim() || undefined,
      });
    },
    onSuccess: () => {
      toast.success('Requirement created');
      void queryClient.invalidateQueries({ queryKey: ['requirements', 'list'] });
      void queryClient.invalidateQueries({ queryKey: ['requirements', 'stats'] });
      onClose();
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-full max-w-2xl max-h-[90vh] overflow-y-auto" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>New Requirement</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit((v) => mutation.mutate(v))} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="req-title">Title *</Label>
            <Input
              id="req-title"
              {...register('title', { required: 'Title is required' })}
              placeholder="e.g. 10 Laptops for new developers"
            />
            {errors.title && <p className="text-xs text-destructive">{errors.title.message}</p>}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="req-description">Description</Label>
            <Textarea
              id="req-description"
              {...register('description')}
              placeholder="Detailed description of the requirement"
              rows={3}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Category *</Label>
              <Select
                value={categoryValue}
                onValueChange={(v) => setValue('category_id', v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.category_id && <p className="text-xs text-destructive">{errors.category_id.message}</p>}
              {/* hidden input for RHF tracking */}
              <input type="hidden" {...register('category_id', { required: 'Category is required' })} />
            </div>

            <div className="space-y-1.5">
              <Label>Priority</Label>
              <Select
                value={priorityValue}
                onValueChange={(v) => setValue('priority', v as FormValues['priority'])}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label htmlFor="req-qty">Quantity</Label>
              <Input
                id="req-qty"
                type="number"
                min={1}
                step="any"
                {...register('quantity', { valueAsNumber: true, min: { value: 1, message: 'Min 1' } })}
              />
              {errors.quantity && <p className="text-xs text-destructive">{errors.quantity.message}</p>}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="req-unit">Unit</Label>
              <Input
                id="req-unit"
                {...register('unit')}
                placeholder="pieces"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="req-cost">Estimated Cost (₹)</Label>
              <Input
                id="req-cost"
                type="number"
                min={0}
                step="any"
                {...register('estimated_cost')}
                placeholder="0.00"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="req-due">Due Date</Label>
            <Input id="req-due" type="date" {...register('due_date')} />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="req-notes">Notes</Label>
            <Textarea
              id="req-notes"
              {...register('notes')}
              placeholder="Additional notes or specifications"
              rows={2}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Create Requirement
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
